// src/pages/shipment-tracking-details-dashboard/components/ShipmentDetailPanel.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ShipmentDetailPanel = ({ shipment, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatWeight = (weight) => {
    return new Intl.NumberFormat('en-US').format(weight) + ' kg';
  };

  const formatCarbonFootprint = (carbonData) => {
    if (!carbonData?.total) return 'N/A';
    return `${carbonData.total} ${carbonData.unit || 'tonnes CO2e'}`;
  };

  const getCarbonFootprintColor = (carbonData) => {
    if (!carbonData?.total) return 'text-text-secondary';
    if (carbonData.total < 15) return 'text-success';
    if (carbonData.total < 25) return 'text-warning';
    return 'text-error';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Transit': return 'bg-secondary-50 text-secondary-700 border-secondary-100';
      case 'Delayed': return 'bg-warning-50 text-warning-700 border-warning-100';
      case 'At Port': return 'bg-primary-50 text-primary-700 border-primary-100';
      case 'Critical Delay': return 'bg-error-50 text-error-700 border-error-100';
      case 'Delivered': return 'bg-success-50 text-success-700 border-success-100';
      default: return 'bg-surface-50 text-text-secondary border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-text-secondary';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Package' },
    { id: 'tracking', label: 'Tracking', icon: 'MapPin' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'sustainability', label: 'Sustainability', icon: 'Leaf' }
  ];

  return (
    <div className="bg-surface rounded-lg border border-border shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icon name="Package" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                {shipment.trackingId}
              </h2>
              <p className="text-sm text-text-secondary">
                {shipment.customer} • {shipment.carrier}
              </p>
            </div>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(shipment.currentStatus)}`}>
            {shipment.currentStatus}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-text-secondary hover:text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border">
        <nav className="flex space-x-8 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors duration-150
                ${activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-primary hover:border-primary-200'
                }
              `}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Key Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="MapPin" size={16} className="text-primary" />
                  <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">Route</h3>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-text-primary">{shipment.origin}</p>
                  <div className="flex items-center space-x-1 text-text-secondary">
                    <Icon name="ArrowRight" size={12} />
                    <span className="text-sm">{shipment.destination}</span>
                  </div>
                </div>
              </div>

              <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Clock" size={16} className="text-primary" />
                  <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">ETA</h3>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-text-primary">{formatDate(shipment.eta)}</p>
                  <p className="text-xs text-text-secondary">Current: {shipment.currentLocation.port}</p>
                </div>
              </div>

              <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Flag" size={16} className="text-primary" />
                  <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">Priority</h3>
                </div>
                <div className="space-y-1">
                  <p className={`text-sm font-medium capitalize ${getPriorityColor(shipment.priority)}`}>
                    {shipment.priority}
                  </p>
                  <div className="flex items-center space-x-1">
                    <div className={`w-2 h-2 rounded-full ${
                      shipment.priority === 'high' ? 'bg-error' :
                      shipment.priority === 'medium' ? 'bg-warning' : 'bg-success'
                    }`}></div>
                    <span className="text-xs text-text-secondary">Priority level</span>
                  </div>
                </div>
              </div>

              <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="DollarSign" size={16} className="text-primary" />
                  <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">Value</h3>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-text-primary">{formatCurrency(shipment.value)}</p>
                  <p className="text-xs text-text-secondary">{shipment.containerCount} containers</p>
                </div>
              </div>

              <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Package" size={16} className="text-primary" />
                  <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">Container</h3>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-text-primary">{shipment.containerType}</p>
                  <p className="text-xs text-text-secondary">{formatWeight(shipment.weight)}</p>
                </div>
              </div>

              <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Leaf" size={16} className="text-primary" />
                  <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">Carbon Footprint</h3>
                </div>
                <div className="space-y-1">
                  <p className={`text-sm font-medium ${getCarbonFootprintColor(shipment.carbonFootprint)}`}>
                    {formatCarbonFootprint(shipment.carbonFootprint)}
                  </p>
                  {shipment.carbonFootprint?.offsetAvailable ? (
                    <div className="flex items-center space-x-1">
                      <Icon name="Shield" size={12} className="text-success" />
                      <span className="text-xs text-success">Offset available</span>
                    </div>
                  ) : (
                    <p className="text-xs text-text-secondary">No offset available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Shipment Notes */}
            {shipment.notes && (
              <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="FileText" size={16} className="text-primary" />
                  <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">Notes</h3>
                </div>
                <p className="text-sm text-text-primary whitespace-pre-line">{shipment.notes}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'tracking' && (
          <div className="space-y-6">
            {/* Current Location */}
            <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="MapPin" size={16} className="text-primary" />
                <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">Current Location</h3>
              </div>
              <p className="text-lg font-medium text-text-primary">
                {shipment.currentLocation.port}, {shipment.currentLocation.country}
              </p>
              <p className="text-sm text-text-secondary mt-1">
                Coordinates: {shipment.currentLocation.lat}, {shipment.currentLocation.lng}
              </p>
            </div>

            {/* Tracking Timeline */}
            <div>
              <h3 className="text-lg font-medium text-text-primary mb-4">Tracking Timeline</h3>
              <div className="space-y-4">
                {shipment.milestones.map((milestone, index) => (
                  <div key={milestone.id} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className={`
                        w-3 h-3 rounded-full border-2
                        ${milestone.completed 
                          ? milestone.current 
                            ? 'bg-primary border-primary animate-pulse' :'bg-success border-success' :'bg-surface border-border-light'
                        }
                      `}></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-medium ${
                          milestone.completed ? 'text-text-primary' : 'text-text-secondary'
                        }`}>
                          {milestone.status}
                        </p>
                        <p className="text-xs text-text-secondary">
                          {formatDate(milestone.date)}
                        </p>
                      </div>
                      {milestone.current && (
                        <p className="text-xs text-primary mt-1 font-medium">Current Status</p>
                      )}
                    </div>
                    {index < shipment.milestones.length - 1 && (
                      <div className="absolute left-6 mt-4 w-px h-8 bg-border-light"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-text-primary">Shipment Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {shipment.documents.map((doc) => (
                <div key={doc.id} className="bg-surface-50 rounded-lg p-4 border border-border-light hover:border-primary-200 transition-colors duration-150">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary-50 rounded-md">
                        <Icon name="FileText" size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{doc.name}</p>
                        <p className="text-xs text-text-secondary">
                          Uploaded: {formatDate(doc.uploadDate)}
                        </p>
                      </div>
                    </div>
                    <button className="p-2 text-text-secondary hover:text-primary transition-colors duration-150">
                      <Icon name="Download" size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'sustainability' && (
          <div className="space-y-6">
            <div className="bg-surface-50 rounded-lg p-6 border border-border-light">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="Leaf" size={20} className="text-success" />
                <h3 className="text-lg font-medium text-text-primary">Environmental Impact</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary uppercase tracking-wide mb-3">Carbon Footprint</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Total Emissions:</span>
                      <span className={`text-sm font-medium ${getCarbonFootprintColor(shipment.carbonFootprint)}`}>
                        {formatCarbonFootprint(shipment.carbonFootprint)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Per Kilometer:</span>
                      <span className="text-sm font-medium text-text-primary">
                        {shipment.carbonFootprint?.perKm || 'N/A'} tonnes CO2e/km
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-secondary">Efficiency Rating:</span>
                      <div className="flex items-center space-x-2">
                        {shipment.carbonFootprint?.total < 15 ? (
                          <>
                            <div className="w-2 h-2 rounded-full bg-success"></div>
                            <span className="text-sm font-medium text-success">Excellent</span>
                          </>
                        ) : shipment.carbonFootprint?.total < 25 ? (
                          <>
                            <div className="w-2 h-2 rounded-full bg-warning"></div>
                            <span className="text-sm font-medium text-warning">Good</span>
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 rounded-full bg-error"></div>
                            <span className="text-sm font-medium text-error">Needs Improvement</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-text-secondary uppercase tracking-wide mb-3">Carbon Offset</h4>
                  <div className="space-y-3">
                    {shipment.carbonFootprint?.offsetAvailable ? (
                      <>
                        <div className="flex items-center space-x-2 text-success">
                          <Icon name="Shield" size={16} />
                          <span className="text-sm font-medium">Offset Available</span>
                        </div>
                        <p className="text-sm text-text-secondary">
                          This shipment is eligible for carbon offset programs. 
                          Purchase verified carbon credits to neutralize emissions.
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-text-secondary">Estimated Cost:</span>
                            <span className="font-medium text-text-primary">
                              ${((shipment.carbonFootprint?.total || 0) * 20).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-text-secondary">Rate:</span>
                            <span className="font-medium text-text-primary">$20/tonne CO2e</span>
                          </div>
                        </div>
                        <button className="w-full bg-success text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-success-600 transition-colors duration-150 flex items-center justify-center space-x-2">
                          <Icon name="CreditCard" size={16} />
                          <span>Purchase Carbon Offset</span>
                        </button>
                        <button className="w-full border border-border text-text-primary py-2 px-4 rounded-md text-sm font-medium hover:bg-surface-50 transition-colors duration-150 flex items-center justify-center space-x-2">
                          <Icon name="Calculator" size={16} />
                          <span>Calculate Custom Offset</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center space-x-2 text-text-secondary">
                          <Icon name="X" size={16} />
                          <span className="text-sm font-medium">No Offset Available</span>
                        </div>
                        <p className="text-sm text-text-secondary">
                          Carbon offset is not currently available for this shipment route.
                          Contact support for alternative sustainability options.
                        </p>
                        <button className="w-full border border-border text-text-primary py-2 px-4 rounded-md text-sm font-medium hover:bg-surface-50 transition-colors duration-150 flex items-center justify-center space-x-2">
                          <Icon name="Mail" size={16} />
                          <span>Contact Support</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Sustainability Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-success-50 rounded-lg p-4 border border-success-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Recycle" size={16} className="text-success" />
                  <span className="text-sm font-medium text-success uppercase tracking-wide">Eco-Efficiency</span>
                </div>
                <p className="text-2xl font-bold text-success">
                  {shipment.carbonFootprint?.total < 15 ? '85%' : shipment.carbonFootprint?.total < 25 ? '72%' : '58%'}
                </p>
                <p className="text-xs text-success mt-1">vs industry average</p>
              </div>
              
              <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Truck" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-primary uppercase tracking-wide">Route Efficiency</span>
                </div>
                <p className="text-2xl font-bold text-primary">92%</p>
                <p className="text-xs text-primary mt-1">optimal routing</p>
              </div>
              
              <div className="bg-warning-50 rounded-lg p-4 border border-warning-100">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="Target" size={16} className="text-warning" />
                  <span className="text-sm font-medium text-warning uppercase tracking-wide">Fuel Efficiency</span>
                </div>
                <p className="text-2xl font-bold text-warning">8.2L</p>
                <p className="text-xs text-warning mt-1">per 100km</p>
              </div>
            </div>

            <div className="bg-surface-50 rounded-lg p-4 border border-border-light">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Info" size={16} className="text-primary" />
                <h4 className="text-sm font-medium text-text-secondary uppercase tracking-wide">Sustainability Tips</h4>
              </div>
              <ul className="text-sm text-text-secondary space-y-2">
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={12} className="text-success mt-1 flex-shrink-0" />
                  <span>Consider consolidating shipments to reduce overall carbon footprint</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={12} className="text-success mt-1 flex-shrink-0" />
                  <span>Choose sea freight over air freight when possible for lower emissions</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={12} className="text-success mt-1 flex-shrink-0" />
                  <span>Partner with carriers committed to sustainable shipping practices</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={12} className="text-success mt-1 flex-shrink-0" />
                  <span>Implement carbon offset programs for all high-emission routes</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShipmentDetailPanel;